import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

// Shipment interfaces based on the MPAD Phase 2 implementation with enhanced context
export interface Shipment {
  id: number;
  shipment_reference: string;
  description?: string;
  priority: number;
  pickup_location_id: number;
  delivery_location_id: number;
  pickup_time_window_start: number;
  pickup_time_window_end: number;
  delivery_time_window_start: number;
  delivery_time_window_end: number;
  // Setup and Service Times (minutes)
  pickup_setup: number;
  pickup_service: number;
  delivery_setup: number;
  delivery_service: number;
  is_direct_delivery: boolean;
  requires_same_vehicle: boolean;
  max_transit_time?: number;
  project_id: number;
  // MPAD Enhancement: Scenario/Dataset context
  scenario_id?: number;
  dataset_id?: number | null;
  dataset_name?: string;
  dataset_version?: number;
  // Enhanced datetime time windows
  pickup_time_window_start_dt?: string;
  pickup_time_window_end_dt?: string;
  delivery_time_window_start_dt?: string;
  delivery_time_window_end_dt?: string;
  pickup_timezone?: string;
  delivery_timezone?: string;
  // Data lineage
  source_shipment_id?: number | null;
  is_derived?: boolean;
  derivation_metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface ShipmentCreate {
  shipment_reference: string;
  description?: string;
  priority: number;
  pickup_location_id: number;
  delivery_location_id: number;
  pickup_time_window_start: number;
  pickup_time_window_end: number;
  delivery_time_window_start: number;
  delivery_time_window_end: number;
  // Setup and Service Times (minutes)
  pickup_setup: number;
  pickup_service: number;
  delivery_setup: number;
  delivery_service: number;
  is_direct_delivery: boolean;
  requires_same_vehicle: boolean;
  max_transit_time?: number;
  products: Array<{
    product_id: number;
    quantity: number;
    loading_sequence?: number;
    special_instructions?: string;
  }>;
  // MPAD Enhancement: Scenario/Dataset context (required for proper data organization)
  scenario_id: number; // Required by Edge Function
  dataset_id?: number | null; // Optional dataset context
  dataset_name?: string; // Dataset name for identification
  dataset_version?: number; // Dataset version for tracking
  // Enhanced datetime time windows (optional, will use legacy format if not provided)
  pickup_time_window_start_dt?: string; // ISO datetime string
  pickup_time_window_end_dt?: string; // ISO datetime string
  delivery_time_window_start_dt?: string; // ISO datetime string
  delivery_time_window_end_dt?: string; // ISO datetime string
  pickup_timezone?: string; // Timezone for pickup (defaults to UTC)
  delivery_timezone?: string; // Timezone for delivery (defaults to UTC)
  shipment_date?: string; // Base date for time window calculations
  // Data lineage tracking
  source_shipment_id?: number | null; // Reference to original shipment if derived
  is_derived?: boolean; // Flag for derived shipments
  derivation_metadata?: Record<string, any>; // Metadata about derivation
}

export interface ShipmentUpdate {
  shipment_reference?: string;
  description?: string;
  priority?: number;
  pickup_location_id?: number;
  delivery_location_id?: number;
  pickup_time_window_start?: number;
  pickup_time_window_end?: number;
  delivery_time_window_start?: number;
  delivery_time_window_end?: number;
  is_direct_delivery?: boolean;
  requires_same_vehicle?: boolean;
  max_transit_time?: number;
}

export interface ShipmentListResponse {
  shipments: Shipment[];
  total_count: number;
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// ===============================================
// SHIPMENTS HOOKS
// ===============================================

/**
 * Hook to get all shipments with filtering and search
 */
export const useShipments = (
  projectId: number,
  options?: {
    priority_min?: number;
    priority_max?: number;
    is_direct_delivery?: boolean;
    requires_same_vehicle?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: 'shipment_reference' | 'priority' | 'created_at';
    sort_order?: 'asc' | 'desc';
  }
) => {
  return useQuery<ShipmentListResponse, Error>({
    queryKey: ['shipments', projectId, options],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          project_id: projectId.toString(),
          ...(options?.priority_min && { priority_min: options.priority_min.toString() }),
          ...(options?.priority_max && { priority_max: options.priority_max.toString() }),
          ...(options?.is_direct_delivery !== undefined && { 
            is_direct_delivery: options.is_direct_delivery.toString() 
          }),
          ...(options?.requires_same_vehicle !== undefined && { 
            requires_same_vehicle: options.requires_same_vehicle.toString() 
          }),
          ...(options?.search && { search: options.search }),
          ...(options?.limit && { limit: options.limit.toString() }),
          ...(options?.offset && { offset: options.offset.toString() }),
          ...(options?.sort_by && { sort_by: options.sort_by }),
          ...(options?.sort_order && { sort_order: options.sort_order }),
        });

        // Get JWT token from Supabase auth
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch(
          `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/shipments?${params}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            }
          }
        );
        
        if (!response.ok) {
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          throw new Error(`Failed to fetch shipments: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching shipments:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 3;
    },
  });
};

/**
 * Hook to get a single shipment by ID
 */
export const useShipment = (shipmentId: number) => {
  return useQuery<Shipment, Error>({
    queryKey: ['shipment', shipmentId],
    queryFn: async () => {
      try {
        // Get JWT token from Supabase auth
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch(
          `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/shipments/${shipmentId}`,
          {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          }
        );
        
        if (!response.ok) {
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Shipment not found');
          throw new Error(`Failed to fetch shipment: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching shipment:', error);
        throw error;
      }
    },
    enabled: !!shipmentId && shipmentId > 0,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook to create a new shipment
 */
export const useCreateShipment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Shipment, Error, { shipment: ShipmentCreate; projectId: number }>({
    mutationFn: async ({ shipment, projectId }) => {
      try {
        // Get JWT token from Supabase auth
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No authentication token available');
        }

        console.log('🚀 Edge Function Call:', {
          url: `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/shipments?project_id=${projectId}`,
          method: 'POST',
          hasAuth: true
        });

        const response = await fetch(
          `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/shipments?project_id=${projectId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify(shipment)
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 409) throw new Error('Shipment reference already exists in project');
          if (response.status === 422) {
            throw new Error(errorData?.detail || 'Validation error: Please check your input');
          }
          throw new Error(errorData?.detail || `Failed to create shipment: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('⚡ shipments?project_id=' + projectId + ' response: 201 in XXXms');
        console.log('Shipment created successfully in database');
        
        return result;
      } catch (error) {
        console.error('Error creating shipment:', error);
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to create shipment');
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate shipments list to refetch with new shipment
      queryClient.invalidateQueries({ queryKey: ['shipments', variables.projectId] });
      // Add the new shipment to the cache
      queryClient.setQueryData(['shipment', data.id], data);
    },
    onError: (error) => {
      console.error('Error saving shipment:', error);
    },
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook to update a shipment
 */
export const useUpdateShipment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Shipment, Error, { id: number; updates: ShipmentUpdate }>({
    mutationFn: async ({ id, updates }) => {
      try {
        // Get JWT token from Supabase auth
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch(
          `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/shipments/${id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify(updates)
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Shipment not found');
          if (response.status === 422) {
            throw new Error(errorData?.detail || 'Validation error: Please check your input');
          }
          throw new Error(errorData?.detail || `Failed to update shipment: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to update shipment');
      }
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['shipment', id] });
      const previousShipment = queryClient.getQueryData(['shipment', id]);
      return { previousShipment };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['shipment', variables.id], data);
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
    onError: (error, variables, context) => {
      if (context?.previousShipment) {
        queryClient.setQueryData(['shipment', variables.id], context.previousShipment);
      }
      console.error('Error updating shipment:', error);
    },
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook to delete a shipment
 */
export const useDeleteShipment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, number>({
    mutationFn: async (shipmentId: number) => {
      try {
        // Get JWT token from Supabase auth
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch(
          `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/shipments/${shipmentId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Shipment not found');
          if (response.status === 409) throw new Error('Cannot delete shipment: It is currently in use');
          throw new Error(errorData?.detail || `Failed to delete shipment: ${response.status}`);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to delete shipment');
      }
    },
    onMutate: async (shipmentId) => {
      await queryClient.cancelQueries({ queryKey: ['shipment', shipmentId] });
      await queryClient.cancelQueries({ queryKey: ['shipments'] });
    },
    onSuccess: (_, shipmentId) => {
      queryClient.removeQueries({ queryKey: ['shipment', shipmentId] });
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
    onError: (error) => {
      console.error('Error deleting shipment:', error);
    },
    retry: false,
  });
};

// ===============================================
// UTILITY FUNCTIONS
// ===============================================

/**
 * Validate shipment data before submission
 */
export const validateShipment = (shipment: ShipmentCreate): string[] => {
  const errors: string[] = [];
  
  if (!shipment.shipment_reference.trim()) {
    errors.push('Shipment reference is required');
  }
  
  if (shipment.priority < 1 || shipment.priority > 10) {
    errors.push('Priority must be between 1 and 10');
  }
  
  if (!shipment.pickup_location_id) {
    errors.push('Pickup location is required');
  }
  
  if (!shipment.delivery_location_id) {
    errors.push('Delivery location is required');
  }
  
  if (shipment.pickup_location_id === shipment.delivery_location_id) {
    errors.push('Pickup and delivery locations must be different');
  }
  
  if (shipment.pickup_time_window_end <= shipment.pickup_time_window_start) {
    errors.push('Pickup end time must be after start time');
  }
  
  if (shipment.delivery_time_window_end <= shipment.delivery_time_window_start) {
    errors.push('Delivery end time must be after start time');
  }
  
  if (!shipment.products || shipment.products.length === 0) {
    errors.push('At least one product is required');
  }
  
  shipment.products.forEach((product, index) => {
    if (product.quantity <= 0) {
      errors.push(`Product ${index + 1}: Quantity must be positive`);
    }
  });
  
  if (shipment.max_transit_time && shipment.max_transit_time <= 0) {
    errors.push('Maximum transit time must be positive');
  }
  
  return errors;
};

/**
 * Format time window for display
 */
export const formatTimeWindow = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Calculate shipment capacity requirements
 */
export const calculateShipmentCapacity = (
  products: Array<{ product_id: number; quantity: number; product?: any }>
): { totalWeight: number; totalVolume: number; totalItems: number } => {
  let totalWeight = 0;
  let totalVolume = 0;
  let totalItems = 0;

  products.forEach(p => {
    if (p.product) {
      totalWeight += (p.product.weight_per_unit_kg || 0) * p.quantity;
      totalVolume += (p.product.volume_per_unit_m3 || 0) * p.quantity;
      totalItems += p.quantity;
    }
  });

  return { totalWeight, totalVolume, totalItems };
};